import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  RefreshCw, ChevronsLeft, ChevronLeft, ChevronRight, ChevronsRight, Filter, MapPin, ExternalLink, List,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { statusLabel } from "@/lib/constants";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import ColumnPicker, { useColumnPrefs } from "@/components/ColumnPicker";

// ---- date helpers --------------------------------------------------------
const toISODate = (d) => {
  if (!d) return null;
  const x = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(x.getTime())) return null;
  return x.toISOString().slice(0, 10);
};
const todayISO = () => toISODate(new Date());
const shiftDays = (iso, days) => {
  const d = new Date(iso + "T00:00:00");
  d.setDate(d.getDate() + days);
  return toISODate(d);
};
const fmtDateTime = (v) => {
  if (!v) return "";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return v;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dy = String(d.getDate()).padStart(2, "0");
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${y}-${m}-${dy} ${hh}:${mm}`;
};
// jobDate: prefer pickup, then appointment, then delivery
const jobDate = (j) => (j.pickup_date || j.appointment_date || j.delivery_date || "").slice(0, 10) || null;
// A job is "closed" (goes into Past) when it is delivered, invoiced, or cancelled.
const CLOSED_STATUSES = new Set(["delivered", "invoiced", "cancelled"]);

// Status pill styling
const statusCell = (s) => {
  const label = statusLabel(s);
  const map = {
    requested: "text-amber-800",
    quoted: "text-blue-800",
    scheduled: "text-indigo-800",
    in_transit: "text-sky-800",
    delivered: "text-emerald-800",
    invoiced: "text-zinc-900",
    cancelled: "text-rose-800",
  };
  return <span className={map[s] || "text-zinc-900"}>{label}</span>;
};

const TABS = [
  { key: "work_orders", label: "Work Orders" },
  { key: "assigned",    label: "Assigned" },
  { key: "in_progress", label: "In Progress" },
  { key: "completed",   label: "Completed" },
  { key: "past",        label: "Past" },
];

// filterByTab now uses CLOSED_STATUSES so completed jobs never leak into Work Orders
const filterByTab = (jobs, tab) => {
  switch (tab) {
    case "assigned":
      return jobs.filter((j) => (j.driver_name || j.carrier_id) && !CLOSED_STATUSES.has(j.status));
    case "in_progress":
      return jobs.filter((j) => j.status === "in_transit");
    case "completed":
      return jobs.filter((j) => j.status === "delivered" || j.status === "invoiced");
    case "past":
      // Truly past: status is delivered/invoiced/cancelled OR delivery_date is before today
      return jobs.filter((j) => {
        if (CLOSED_STATUSES.has(j.status)) return true;
        const dd = (j.delivery_date || "").slice(0, 10);
        return dd && dd < todayISO();
      });
    case "work_orders":
    default:
      // Work Orders = all ACTIVE jobs (not closed) — ignore date filter here so
      // the user can see all outstanding work regardless of pickup date.
      return jobs.filter((j) => !CLOSED_STATUSES.has(j.status));
  }
};

// ---- column configs -------------------------------------------------------
const WO_COLS = [
  { key: "job_number",     label: "Job #" },
  { key: "customer_name",  label: "Customer" },
  { key: "carrier_name",   label: "Carrier" },
  { key: "status",         label: "Status" },
  { key: "lc_emailed",     label: "LC Emailed" },
  { key: "pickup_date",    label: "Pickup" },
  { key: "delivery_date",  label: "Delivery" },
  { key: "pickup_name",    label: "Pickup Location" },
  { key: "pickup_city",    label: "Pick City" },
  { key: "delivery_name",  label: "Dropoff Location" },
  { key: "delivery_city",  label: "Drop City" },
  { key: "tracking",       label: "Tracking" },
];

// ---- toolbar checkbox chip -----------------------------------------------
const CheckChip = ({ id, label, checked, onChange, testId }) => (
  <label className="flex items-center gap-1.5 text-[11px] text-zinc-800 cursor-pointer select-none">
    <Checkbox id={id} checked={checked} onCheckedChange={onChange} className="h-3.5 w-3.5 rounded-none" data-testid={testId} />
    <span>{label}</span>
  </label>
);

// ---- panel primitives ----------------------------------------------------
const Panel = ({ children, className = "" }) => (
  <div className={`bg-white border border-zinc-400 ${className}`}>{children}</div>
);
const PanelHeader = ({ children, right }) => (
  <div className="flex items-center justify-between px-2 h-7 border-b border-zinc-400 bg-[#f5f5f5]">
    <div className="flex items-center gap-3 text-[11px]">{children}</div>
    {right}
  </div>
);

// -------------------------------------------------------------------------
export default function DispatchBoard() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [carriers, setCarriers] = useState([]);

  // Toolbar state
  const [dateISO, setDateISO] = useState(todayISO());
  const [range, setRange] = useState(false);
  const [rangeEndISO, setRangeEndISO] = useState(todayISO());
  const [dateActive, setDateActive] = useState(false); // when unchecked, ignore date filter entirely
  const [activeOnlyCarriers, setActiveOnlyCarriers] = useState(false);

  // Main grid state
  const [tab, setTab] = useState("work_orders");
  const [selectedJobId, setSelectedJobId] = useState(null);
  const [selectedCarrierId, setSelectedCarrierId] = useState(null);
  const [alertJobId, setAlertJobId] = useState(null);
  const [bottomView, setBottomView] = useState("list"); // list | map

  const woPrefs = useColumnPrefs("apex.dispatch.wo_cols_v2", WO_COLS);
  const woCols = woPrefs.visibleColumns;

  // ---- data load ---------------------------------------------------------
  const load = async () => {
    setLoading(true);
    try {
      const [{ data: js }, carriersRes] = await Promise.all([
        api.get("/jobs"),
        api.get("/carriers").catch(() => ({ data: [] })),
      ]);
      setJobs(js);
      setCarriers(carriersRes.data || []);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const allowedDates = useMemo(() => {
    if (!dateActive) return null; // date filter disabled entirely
    const s = new Set();
    if (range) {
      let cur = dateISO;
      let guard = 0;
      while (cur <= rangeEndISO && guard++ < 366) {
        s.add(cur);
        cur = shiftDays(cur, 1);
      }
      return s;
    }
    s.add(dateISO);
    return s;
  }, [dateActive, range, dateISO, rangeEndISO]);

  const drivers = useMemo(() => {
    const jobCountByCarrierId = new Map();
    const jobCountByCarrierName = new Map();
    jobs.forEach((j) => {
      if (CLOSED_STATUSES.has(j.status)) return; // only active work counts
      if (j.carrier_id) jobCountByCarrierId.set(j.carrier_id, (jobCountByCarrierId.get(j.carrier_id) || 0) + 1);
      else if (j.carrier_name) jobCountByCarrierName.set(j.carrier_name, (jobCountByCarrierName.get(j.carrier_name) || 0) + 1);
    });
    const rows = carriers.map((c) => ({
      key: c.id,
      id: c.id,
      name: c.name,
      mc: c.mc_number || "",
      contact: c.contact_name || "",
      job_count: (jobCountByCarrierId.get(c.id) || 0) + (jobCountByCarrierName.get(c.name) || 0),
      active: c.is_active !== false && (c.is_coi_valid !== false),
    }));
    const savedIds = new Set(carriers.map((c) => c.id));
    const savedNames = new Set(carriers.map((c) => c.name));
    const seen = new Set();
    jobs.forEach((j) => {
      if (j.carrier_name && !savedNames.has(j.carrier_name) && !seen.has(j.carrier_name)
          && !(j.carrier_id && savedIds.has(j.carrier_id))) {
        seen.add(j.carrier_name);
        rows.push({
          key: `unsaved-${j.carrier_name}`,
          id: null,
          name: j.carrier_name,
          mc: j.mc_number || "",
          contact: "",
          job_count: jobCountByCarrierName.get(j.carrier_name) || 1,
          active: true,
        });
      }
    });
    let list = rows.sort((a, b) => a.name.localeCompare(b.name));
    if (activeOnlyCarriers) list = list.filter((c) => c.active);
    return list;
  }, [jobs, carriers, activeOnlyCarriers]);

  // ---- filtered rows for the main grid -----------------------------------
  const filtered = useMemo(() => {
    let rows = jobs.slice();

    // Date filter (only when explicitly enabled AND not on the Work Orders tab —
    // Work Orders should always show all active jobs per user's request).
    if (allowedDates && tab !== "past" && tab !== "work_orders") {
      rows = rows.filter((j) => {
        const d = jobDate(j);
        if (!d) return allowedDates.has(dateISO);
        return allowedDates.has(d);
      });
    }

    // Carrier filter (left panel)
    if (selectedCarrierId) {
      const c = carriers.find((x) => x.id === selectedCarrierId);
      const name = c?.name || selectedCarrierId;
      rows = rows.filter((j) => j.carrier_id === selectedCarrierId || (j.carrier_name && j.carrier_name === name));
    }

    rows = filterByTab(rows, tab);
    rows.sort((a, b) => {
      const da = jobDate(a) || "9999-99-99";
      const dbb = jobDate(b) || "9999-99-99";
      if (da !== dbb) return da.localeCompare(dbb);
      return (a.job_number || "").localeCompare(b.job_number || "");
    });
    return rows;
  }, [jobs, tab, allowedDates, dateISO, selectedCarrierId, carriers]);

  // Live-tracking rows = every ACTIVE job (in-transit or scheduled) with a carrier.
  const trackedJobs = useMemo(() => {
    return jobs
      .filter((j) => !CLOSED_STATUSES.has(j.status) && (j.carrier_id || j.carrier_name))
      .sort((a, b) => (jobDate(a) || "").localeCompare(jobDate(b) || ""));
  }, [jobs]);

  // Weight/pallet numbers
  const totals = useMemo(() => {
    const j = selectedJobId ? jobs.find((x) => x.id === selectedJobId) : null;
    if (!j) return { weight: 0, pallets: 0, hasSelection: false };
    return {
      weight: Number(j.weight) || 0,
      pallets: (j.pieces_unit || "").toLowerCase().includes("pallet") ? (Number(j.pieces) || 0) : 0,
      pieces: Number(j.pieces) || 0,
      pieces_unit: j.pieces_unit || "",
      hasSelection: true,
    };
  }, [jobs, selectedJobId]);

  const alertJob = alertJobId ? jobs.find((j) => j.id === alertJobId) : null;

  const doRefresh = () => load();

  const openTracking = (j) => {
    if (j.tracking_url) window.open(j.tracking_url, "_blank");
    else {
      const q = encodeURIComponent(`${j.delivery?.street || ""} ${j.delivery?.city || ""} ${j.delivery?.state || ""}`.trim());
      window.open(`https://www.google.com/maps/search/?api=1&query=${q}`, "_blank");
    }
  };

  if (loading) return <div className="mono-label">Loading dispatch board…</div>;

  return (
    <div
      className="bg-[#ececec] border border-zinc-400 -mx-4 -my-4 md:-mx-6 md:-my-6 min-h-[calc(100vh-4rem)]"
      style={{ fontFamily: "'Segoe UI', 'IBM Plex Sans', system-ui, sans-serif" }}
      data-testid="dispatch-board"
    >
      {/* ───────────────────── TOP TOOLBAR ───────────────────── */}
      <div className="border-b border-zinc-400 bg-[#ececec] px-2 py-1.5 flex flex-wrap items-center gap-x-3 gap-y-2 text-[11px]">
        <Button
          onClick={doRefresh}
          className="sharp h-9 px-3 bg-white text-zinc-900 hover:bg-zinc-100 border border-zinc-400 text-[12px] font-semibold"
          data-testid="dispatch-refresh"
        >
          <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Refresh
        </Button>

        <div className="flex flex-col gap-0.5 border-l border-zinc-400 pl-3">
          <div className="flex items-center gap-3">
            <CheckChip id="date-active" label="Filter by Date" checked={dateActive} onChange={setDateActive} testId="chk-date-active" />
            <CheckChip id="range" label="Range" checked={range} onChange={setRange} testId="chk-range" />
          </div>
          <div className="flex items-center gap-1">
            <Input type="date" value={dateISO} onChange={(e) => setDateISO(e.target.value)} disabled={!dateActive}
              className="sharp h-6 w-32 text-[11px] px-1 py-0 bg-white border-zinc-400 disabled:opacity-50" data-testid="date-selected" />
            {range && (
              <Input type="date" value={rangeEndISO} onChange={(e) => setRangeEndISO(e.target.value)} disabled={!dateActive}
                className="sharp h-6 w-32 text-[11px] px-1 py-0 bg-white border-zinc-400 disabled:opacity-50" data-testid="date-range-end" />
            )}
          </div>
        </div>

        {/* Alert box */}
        <div className="ml-auto bg-[#e6f2fb] border border-[#8ac6ec] text-[11px] leading-tight text-zinc-900 px-2 py-1 w-80 min-h-[62px]" data-testid="alert-box">
          {alertJob ? (
            <div className="space-y-0.5">
              <div className="flex items-center justify-between">
                <Link to={`/admin/jobs/${alertJob.id}`} className="font-semibold text-[#0056B3] underline underline-offset-2 font-mono" data-testid="alert-job-link">
                  {alertJob.job_number}
                </Link>
                <span className="uppercase font-mono text-[10px] px-1 border border-zinc-400 bg-white">
                  {statusLabel(alertJob.status)}
                </span>
              </div>
              <div className="grid grid-cols-[52px_1fr] gap-x-2 text-[10.5px]">
                <span className="text-zinc-600">PICKUP</span><span className="uppercase truncate">{alertJob.pickup?.name || alertJob.pickup?.street || "—"}, {alertJob.pickup?.city || ""}</span>
                <span className="text-zinc-600">DROPOFF</span><span className="uppercase truncate">{alertJob.delivery?.name || alertJob.delivery?.street || "—"}, {alertJob.delivery?.city || ""}</span>
                <span className="text-zinc-600">CARRIER</span><span className="truncate">{alertJob.carrier_name || "— unassigned —"}</span>
                <span className="text-zinc-600">APPT</span><span className="font-mono">{alertJob.appointment_date || alertJob.pickup_date || "—"}</span>
                <span className="text-zinc-600">TRACK</span>
                <span className="truncate">
                  {alertJob.tracking_url
                    ? <a href={alertJob.tracking_url} target="_blank" rel="noreferrer" className="text-[#0056B3] underline">Open link</a>
                    : <span className="text-zinc-500">— no link —</span>}
                </span>
              </div>
            </div>
          ) : (
            <>
              <div className="font-semibold uppercase">Dispatch alerts</div>
              <div className="text-[10px] text-zinc-700">Click a Job # to preview details here.</div>
            </>
          )}
        </div>

        {/* Weight / pallets */}
        <div className="bg-[#c8e6ff] border border-[#7cb7de] px-3 py-1 text-[13px] leading-tight min-w-[92px]" data-testid="totals-box" title={totals.hasSelection ? `Selected job: ${alertJob?.job_number || ""}` : "No job selected"}>
          {totals.hasSelection ? (
            <>
              <div className="font-semibold">{totals.weight.toLocaleString()} lbs</div>
              <div>{(totals.pieces_unit || "").toLowerCase().includes("pallet") ? `${totals.pallets} pallets` : (totals.pieces ? `${totals.pieces} ${totals.pieces_unit || "pcs"}` : "0 pallets")}</div>
            </>
          ) : <div className="text-[10px] text-zinc-600 leading-tight">Select a Job # to see weight &amp; pieces here.</div>}
        </div>

        <div className="flex items-center gap-1 text-zinc-700">
          <button className="p-0.5 hover:text-red-600" data-testid="page-first"><ChevronsLeft className="h-4 w-4" /></button>
          <button className="p-0.5 hover:text-red-600"><ChevronLeft className="h-4 w-4" /></button>
          <span className="text-[11px]">1/1</span>
          <button className="p-0.5 hover:text-red-600"><ChevronRight className="h-4 w-4" /></button>
          <button className="p-0.5 hover:text-red-600" data-testid="page-last"><ChevronsRight className="h-4 w-4" /></button>
        </div>
      </div>

      {/* ───────────────────── MIDDLE ROW: Carriers | Work Orders ───────────────────── */}
      <div className="grid grid-cols-[280px_minmax(0,1fr)] gap-1 p-1">
        <Panel className="min-h-[300px]">
          <PanelHeader
            right={
              <div className="flex items-center gap-2 pr-1">
                <CheckChip id="active-only-carriers" label="Active Only" checked={activeOnlyCarriers} onChange={setActiveOnlyCarriers} testId="chk-active-carriers" />
                <button className="text-[11px] text-red-600" title="Clear carrier filter" onClick={() => setSelectedCarrierId(null)} data-testid="clear-carrier-filter">
                  <Filter className="h-3.5 w-3.5" />
                </button>
                <Link to="/admin/carriers" className="text-[11px] text-zinc-700" title="Manage carriers">[+]</Link>
              </div>
            }
          >
            <span className="font-semibold">Carriers</span>
          </PanelHeader>
          <div className="overflow-auto max-h-[62vh]" data-testid="carriers-list">
            <table className="w-full text-[11px]">
              <thead className="bg-[#f5f5f5] sticky top-0">
                <tr className="border-b border-zinc-400 text-zinc-700 text-left">
                  <th className="px-1.5 py-1 border-r border-zinc-400 w-6"></th>
                  <th className="px-1.5 py-1 border-r border-zinc-400">Carrier</th>
                  <th className="px-1.5 py-1 border-r border-zinc-400">MC #</th>
                  <th className="px-1.5 py-1 text-right w-10">Jobs</th>
                </tr>
              </thead>
              <tbody>
                {drivers.map((c) => (
                  <tr key={c.key} onClick={() => setSelectedCarrierId(selectedCarrierId === c.id ? null : c.id)}
                    className={`border-b border-zinc-200 cursor-pointer ${selectedCarrierId && selectedCarrierId === c.id ? "bg-[#cfe4ff]" : "hover:bg-zinc-100"}`}
                    data-testid={`carrier-row-${c.key}`}
                    title={c.contact ? `Contact: ${c.contact}` : c.name}>
                    <td className="px-1.5 py-0.5 border-r border-zinc-200">{c.active ? <span className="inline-block w-2 h-2 rounded-full bg-emerald-500" /> : <span className="inline-block w-2 h-2 rounded-full bg-zinc-300" />}</td>
                    <td className="px-1.5 py-0.5 border-r border-zinc-200 truncate max-w-[10rem]">{c.name}</td>
                    <td className="px-1.5 py-0.5 border-r border-zinc-200 font-mono text-[10.5px]">{c.mc}</td>
                    <td className="px-1.5 py-0.5 text-right font-mono">{c.job_count}</td>
                  </tr>
                ))}
                {drivers.length === 0 && <tr><td colSpan={4} className="px-2 py-3 text-center text-zinc-500">No carriers on file</td></tr>}
              </tbody>
            </table>
          </div>
        </Panel>

        <Panel>
          <div className="flex items-center gap-0 border-b border-zinc-400 bg-[#ececec] text-[11px] overflow-x-auto">
            {TABS.map((t) => (
              <button key={t.key} onClick={() => setTab(t.key)}
                className={`px-3 py-1.5 border-r border-zinc-400 whitespace-nowrap ${tab === t.key ? "bg-white font-semibold" : "hover:bg-zinc-200"}`}
                data-testid={`tab-${t.key}`}>
                {t.label} <span className="ml-1 text-zinc-500 font-mono text-[10px]">({filterByTab(jobs, t.key).length})</span>
              </button>
            ))}
            <div className="ml-auto flex items-center gap-2 pr-2">
              <ColumnPicker columns={WO_COLS} order={woPrefs.order} hidden={woPrefs.hidden}
                onOrderChange={woPrefs.setOrder} onHiddenChange={woPrefs.setHidden} onReset={woPrefs.reset}
                lockedKeys={["job_number", "status"]} buttonSize="xs" testId="wo-columns" />
            </div>
          </div>

          <div className="overflow-auto max-h-[46vh]" data-testid="work-orders-grid">
            <table className="apex-grid" style={{ minWidth: 1000 }}>
              <thead>
                <tr>{woCols.map((c) => <th key={c.key} className="whitespace-nowrap">{c.label}</th>)}</tr>
              </thead>
              <tbody>
                {filtered.map((j) => {
                  const lcSent = !!j.rc_email_sent_at;
                  const cellByKey = {
                    job_number: <Link to={`/admin/jobs/${j.id}`} className="text-[#0056B3] underline underline-offset-2 font-mono" onClick={(e) => e.stopPropagation()} data-testid={`open-job-${j.job_number}`}>{j.job_number}</Link>,
                    customer_name: <span className="truncate">{j.customer_company || j.customer_name}</span>,
                    status: statusCell(j.status),
                    carrier_name: j.carrier_name || <span className="text-zinc-400">—</span>,
                    lc_emailed: j.rc_id ? (
                      <span
                        className={`inline-block px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider border ${
                          lcSent ? "bg-emerald-100 text-emerald-900 border-emerald-300" : "bg-amber-100 text-amber-900 border-amber-300"
                        }`}
                        title={lcSent ? `Sent ${fmtDateTime(j.rc_email_sent_at)}` : "Rate confirmation not yet emailed to carrier"}
                        data-testid={`lc-emailed-${j.job_number}`}
                      >{lcSent ? "Yes" : "No"}</span>
                    ) : <span className="text-zinc-400 text-[10.5px]">— no RC —</span>,
                    pickup_date: <span className="font-mono text-[10.5px]">{fmtDateTime(j.pickup_date)}</span>,
                    delivery_date: <span className="font-mono text-[10.5px]">{fmtDateTime(j.delivery_date)}</span>,
                    pickup_name: <span className="uppercase">{j.pickup?.name || j.pickup?.street || ""}</span>,
                    pickup_city: j.pickup?.city,
                    delivery_name: <span className="uppercase">{j.delivery?.name || j.delivery?.street || ""}</span>,
                    delivery_city: j.delivery?.city,
                    tracking: j.tracking_url ? (
                      <a href={j.tracking_url} target="_blank" rel="noreferrer" className="text-[#0056B3] underline font-mono text-[10.5px] inline-flex items-center" onClick={(e) => e.stopPropagation()}>
                        Track <ExternalLink className="h-3 w-3 ml-0.5" />
                      </a>
                    ) : <span className="text-zinc-400 text-[10.5px]">—</span>,
                  };
                  return (
                    <tr key={j.id} onClick={() => { setSelectedJobId(j.id); setAlertJobId(j.id); }}
                      className={`border-b border-zinc-200 cursor-pointer ${selectedJobId === j.id ? "bg-[#cfe4ff]" : "hover:bg-zinc-100"}`}
                      data-testid={`wo-row-${j.job_number}`}>
                      {woCols.map((c) => <td key={c.key} className="px-1.5 py-0.5 border-r border-zinc-200 whitespace-nowrap">{cellByKey[c.key]}</td>)}
                    </tr>
                  );
                })}
                {filtered.length === 0 && (
                  <tr><td colSpan={woCols.length} className="px-2 py-6 text-center text-zinc-500">
                    {tab === "work_orders" ? "🎉 No active work orders." : "No jobs match this tab."}
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </Panel>
      </div>

      {/* ───────────────────── BOTTOM ROW: Live Tracking (List / Map) ───────────────────── */}
      <div className="p-1 pt-0">
        <Panel className="min-h-[220px]">
          <div className="flex items-center border-b border-zinc-400 bg-[#ececec] text-[11px]">
            <button onClick={() => setBottomView("list")}
              className={`px-3 py-1.5 border-r border-zinc-400 inline-flex items-center gap-1 ${bottomView === "list" ? "bg-white font-semibold" : "hover:bg-zinc-200"}`}
              data-testid="bottom-view-list">
              <List className="h-3 w-3" /> Trips
            </button>
            <button onClick={() => setBottomView("map")}
              className={`px-3 py-1.5 border-r border-zinc-400 inline-flex items-center gap-1 ${bottomView === "map" ? "bg-white font-semibold" : "hover:bg-zinc-200"}`}
              data-testid="bottom-view-map">
              <MapPin className="h-3 w-3" /> Map
            </button>
            <span className="ml-3 text-zinc-500 text-[10.5px]">
              {trackedJobs.length} active shipment{trackedJobs.length === 1 ? "" : "s"} with a carrier
            </span>
          </div>

          {bottomView === "list" ? (
            <div className="overflow-auto max-h-[36vh]" data-testid="trips-grid">
              <table className="apex-grid" style={{ minWidth: 900 }}>
                <thead>
                  <tr>
                    <th>Job #</th>
                    <th>Carrier</th>
                    <th>Driver</th>
                    <th>Truck / Trailer</th>
                    <th>Pickup</th>
                    <th>Delivery</th>
                    <th>Route</th>
                    <th>Tracking</th>
                  </tr>
                </thead>
                <tbody>
                  {trackedJobs.map((j) => (
                    <tr key={j.id} className="border-b border-zinc-200 hover:bg-zinc-100"
                        onClick={() => { setSelectedJobId(j.id); setAlertJobId(j.id); }}>
                      <td className="px-1.5 py-0.5 border-r border-zinc-200">
                        <Link to={`/admin/jobs/${j.id}`} className="text-[#0056B3] underline font-mono" data-testid={`trip-job-${j.job_number}`}>{j.job_number}</Link>
                      </td>
                      <td className="px-1.5 py-0.5 border-r border-zinc-200 truncate max-w-[10rem]">{j.carrier_name || "—"}</td>
                      <td className="px-1.5 py-0.5 border-r border-zinc-200 truncate max-w-[8rem]">{j.driver_name || "—"} {j.driver_phone && <span className="text-zinc-500 text-[10px]">· {j.driver_phone}</span>}</td>
                      <td className="px-1.5 py-0.5 border-r border-zinc-200 font-mono text-[10.5px]">{j.truck_number || "—"} / {j.trailer_number || "—"}</td>
                      <td className="px-1.5 py-0.5 border-r border-zinc-200 font-mono text-[10.5px]">{fmtDateTime(j.pickup_date)}</td>
                      <td className="px-1.5 py-0.5 border-r border-zinc-200 font-mono text-[10.5px]">{fmtDateTime(j.delivery_date)}</td>
                      <td className="px-1.5 py-0.5 border-r border-zinc-200 truncate max-w-[16rem] uppercase text-[10.5px]">
                        {(j.pickup?.city || "—")} → {(j.delivery?.city || "—")}
                      </td>
                      <td className="px-1.5 py-0.5 whitespace-nowrap">
                        <button onClick={(e) => { e.stopPropagation(); openTracking(j); }}
                          className="text-[#0056B3] underline font-mono text-[10.5px] inline-flex items-center"
                          data-testid={`trip-track-${j.job_number}`}>
                          {j.tracking_url ? "Open tracker" : "Map delivery"} <ExternalLink className="h-3 w-3 ml-0.5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                  {trackedJobs.length === 0 && (
                    <tr><td colSpan={8} className="px-2 py-4 text-center text-zinc-500">
                      No active shipments with a carrier assigned. Assign a carrier on a job to see it here.
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          ) : (
            // MAP VIEW — a simple OSM iframe centered on the first tracked job's delivery,
            // plus a per-shipment sidebar so you can click a job and open its tracker.
            <div className="grid grid-cols-[280px_minmax(0,1fr)] max-h-[46vh]" data-testid="map-view">
              <div className="border-r border-zinc-400 overflow-auto text-[11px]">
                {trackedJobs.map((j) => (
                  <div key={j.id}
                       onClick={() => { setSelectedJobId(j.id); setAlertJobId(j.id); }}
                       className={`px-2 py-1.5 border-b border-zinc-200 cursor-pointer ${selectedJobId === j.id ? "bg-[#cfe4ff]" : "hover:bg-zinc-100"}`}>
                    <div className="flex items-center justify-between">
                      <span className="font-mono font-semibold text-[#0056B3]">{j.job_number}</span>
                      <span className="text-[9px] uppercase font-mono text-zinc-600">{statusLabel(j.status)}</span>
                    </div>
                    <div className="text-[10.5px] text-zinc-700 uppercase truncate">
                      {j.pickup?.city || "—"} → {j.delivery?.city || "—"}
                    </div>
                    <div className="text-[10px] text-zinc-500 truncate">{j.carrier_name || "unassigned"}</div>
                    <button onClick={(e) => { e.stopPropagation(); openTracking(j); }}
                      className="mt-0.5 text-[#0056B3] underline text-[10px] inline-flex items-center">
                      {j.tracking_url ? "Open carrier tracker" : "Map delivery in Google Maps"} <ExternalLink className="h-2.5 w-2.5 ml-0.5" />
                    </button>
                  </div>
                ))}
                {trackedJobs.length === 0 && <div className="px-2 py-4 text-center text-zinc-500 text-[11px]">No active shipments.</div>}
              </div>
              <div className="min-h-[240px]">
                {(() => {
                  const target = alertJob || trackedJobs[0];
                  const q = target
                    ? [target.delivery?.street, target.delivery?.city, target.delivery?.state, target.delivery?.zip].filter(Boolean).join(", ")
                    : "";
                  const src = q
                    ? `https://www.openstreetmap.org/export/embed.html?bbox=&layer=mapnik&marker=&query=${encodeURIComponent(q)}`
                    : `https://www.openstreetmap.org/export/embed.html?bbox=-125,40,-75,55&layer=mapnik`;
                  return (
                    <iframe
                      title="Dispatch map"
                      src={src}
                      className="w-full h-full min-h-[240px] border-0"
                      loading="lazy"
                      data-testid="dispatch-map-iframe"
                    />
                  );
                })()}
              </div>
            </div>
          )}
        </Panel>
      </div>
    </div>
  );
}
